// region Languages
import { TFunction } from "@languages/useTranslation";
import { LegacyMigrationMessages } from "./languages/interfaces";
// endregion Languages

import { LegacySource } from "./service";

/**
 * Translation of the codes the migration API returns.
 *
 * The backend answers with codes — never with sentences — precisely so this
 * screen can speak the operator's language. Anything not in these maps is a
 * code added on the backend and not translated here yet, and is shown raw
 * rather than hidden.
 */

/** Separates a reason code from the part that cannot be translated. */
const DETAIL_SEPARATOR = "|";

const OUTCOME_MESSAGE: Record<string, LegacyMigrationMessages> = {
  CREATED: LegacyMigrationMessages.legacyMigrationOutcomeCreated,
  UPDATED: LegacyMigrationMessages.legacyMigrationOutcomeUpdated,
  SKIPPED: LegacyMigrationMessages.legacyMigrationOutcomeSkipped,
  ERROR: LegacyMigrationMessages.legacyMigrationOutcomeError
};

const REASON_MESSAGE: Record<string, LegacyMigrationMessages> = {
  NO_EMAIL: LegacyMigrationMessages.legacyMigrationReasonNoEmail,
  NO_ACCESS_ROWS: LegacyMigrationMessages.legacyMigrationReasonNoAccessRows,
  NO_SSO_ACCESS: LegacyMigrationMessages.legacyMigrationReasonNoSsoAccess,
  IN_OTHER_TENANT: LegacyMigrationMessages.legacyMigrationReasonInOtherTenant,
  TOPCON_STAFF: LegacyMigrationMessages.legacyMigrationReasonTopconStaff,
  ALREADY_PROVISIONED: LegacyMigrationMessages.legacyMigrationReasonAlreadyProvisioned,
  NO_MODULES_SELECTED: LegacyMigrationMessages.legacyMigrationReasonNoModulesSelected,
  REMOVED_FROM_SOURCE: LegacyMigrationMessages.legacyMigrationReasonRemovedFromSource,
  EMAIL_CHANGED: LegacyMigrationMessages.legacyMigrationReasonEmailChanged,
  RESET_EMAIL_FAILED: LegacyMigrationMessages.legacyMigrationReasonResetEmailFailed,
  PROVISION_FAILED: LegacyMigrationMessages.legacyMigrationReasonProvisionFailed,
  INVALID_EMAIL: LegacyMigrationMessages.legacyMigrationReasonInvalidEmail,
  NO_VALID_MODULES: LegacyMigrationMessages.legacyMigrationReasonNoValidModules,
  FLEET_SUPER_ADMIN: LegacyMigrationMessages.legacyMigrationReasonFleetSuperAdmin,
  FLEET_INACTIVE: LegacyMigrationMessages.legacyMigrationReasonFleetInactive
};

const LOCK_REASON_MESSAGE: Record<string, LegacyMigrationMessages> = {
  LEGACY_LOCKED: LegacyMigrationMessages.legacyMigrationLockReasonLocked,
  LEGACY_ALREADY_LOCKED: LegacyMigrationMessages.legacyMigrationLockReasonAlreadyLocked,
  LEGACY_LOCK_SKIPPED_PASSWORD_NOT_KEPT: LegacyMigrationMessages.legacyMigrationLockReasonSkippedPasswordNotKept,
  LEGACY_LOCK_PASSWORD_CHANGED: LegacyMigrationMessages.legacyMigrationLockReasonPasswordChanged,
  LEGACY_LOCK_DENIED: LegacyMigrationMessages.legacyMigrationLockReasonDenied,
  LEGACY_LOCK_FAILED: LegacyMigrationMessages.legacyMigrationLockReasonFailed,
  LEGACY_LOCK_USER_GONE: LegacyMigrationMessages.legacyMigrationLockReasonUserGone
};

/** The same lock codes, worded for a FLEET password. A code missing here reads the same for both sources. */
const FLEET_LOCK_REASON_MESSAGE: Record<string, LegacyMigrationMessages> = {
  ...LOCK_REASON_MESSAGE,
  LEGACY_LOCKED: LegacyMigrationMessages.legacyMigrationFleetLockReasonLocked,
  LEGACY_LOCK_SKIPPED_PASSWORD_NOT_KEPT: LegacyMigrationMessages.legacyMigrationFleetLockReasonSkippedPasswordNotKept,
  LEGACY_LOCK_DENIED: LegacyMigrationMessages.legacyMigrationFleetLockReasonDenied,
  LEGACY_LOCK_FAILED: LegacyMigrationMessages.legacyMigrationFleetLockReasonFailed
};

/**
 * Texts that name the Topcon Legado (the CRM) and their FLEET wording. Plural
 * keys only need the base key here: i18next adds the `_one` / `_other` suffix
 * to whichever key is picked.
 */
const FLEET_MESSAGE: Partial<Record<LegacyMigrationMessages, LegacyMigrationMessages>> = {
  [LegacyMigrationMessages.legacyMigrationSubtitle]: LegacyMigrationMessages.legacyMigrationFleetSubtitle,
  [LegacyMigrationMessages.legacyMigrationChooseHint]: LegacyMigrationMessages.legacyMigrationFleetChooseHint,
  [LegacyMigrationMessages.legacyMigrationReadingHint]: LegacyMigrationMessages.legacyMigrationFleetReadingHint,
  [LegacyMigrationMessages.legacyMigrationReviewHint]: LegacyMigrationMessages.legacyMigrationFleetReviewHint,
  [LegacyMigrationMessages.legacyMigrationReviewLockHint]: LegacyMigrationMessages.legacyMigrationFleetReviewLockHint,
  [LegacyMigrationMessages.legacyMigrationLockNeedsKeptPassword]: LegacyMigrationMessages.legacyMigrationFleetLockNeedsSignIn,
  [LegacyMigrationMessages.legacyMigrationColumnLegacy]: LegacyMigrationMessages.legacyMigrationFleetColumnLegacy,
  [LegacyMigrationMessages.legacyMigrationLockAll]: LegacyMigrationMessages.legacyMigrationFleetLockAll,
  [LegacyMigrationMessages.legacyMigrationExportListLockLegacy]: LegacyMigrationMessages.legacyMigrationFleetExportListLockLegacy,
  [LegacyMigrationMessages.legacyMigrationExportListNewLegacyPassword]:
    LegacyMigrationMessages.legacyMigrationFleetExportListNewPassword,
  [LegacyMigrationMessages.legacyMigrationConfirmLocks]: LegacyMigrationMessages.legacyMigrationFleetConfirmLocks,
  [LegacyMigrationMessages.legacyMigrationLockedSummary]: LegacyMigrationMessages.legacyMigrationFleetLockedSummary,
  [LegacyMigrationMessages.legacyMigrationLockFailed]: LegacyMigrationMessages.legacyMigrationFleetLockFailed,
  [LegacyMigrationMessages.legacyMigrationLockCalloutTitle]: LegacyMigrationMessages.legacyMigrationFleetLockCalloutTitle,
  [LegacyMigrationMessages.legacyMigrationLockHelp]: LegacyMigrationMessages.legacyMigrationFleetLockHelp,
  [LegacyMigrationMessages.legacyMigrationConfirmEmails]: LegacyMigrationMessages.legacyMigrationFleetConfirmEmails,
  [LegacyMigrationMessages.legacyMigrationDoneHint]: LegacyMigrationMessages.legacyMigrationFleetDoneHint
};

/** The key of `message` for this source: its FLEET wording when it names the Topcon Legado. */
export const forSource = (source: LegacySource, message: LegacyMigrationMessages): LegacyMigrationMessages => (
  source === "FLEET" ? FLEET_MESSAGE[message] ?? message : message
);

const ERROR_MESSAGE: Record<string, LegacyMigrationMessages> = {
  LEGACY_MIGRATION_FORBIDDEN: LegacyMigrationMessages.legacyMigrationErrorForbidden,
  LEGACY_MIGRATION_TENANT_NOT_FOUND: LegacyMigrationMessages.legacyMigrationErrorTenantNotFound,
  LEGACY_MIGRATION_DATA_SOURCE_NOT_CONFIGURED: LegacyMigrationMessages.legacyMigrationErrorDataSourceNotConfigured,
  LEGACY_MIGRATION_DATA_SOURCE_NOT_FOUND: LegacyMigrationMessages.legacyMigrationErrorDataSourceNotFound,
  LEGACY_MIGRATION_DATA_SOURCE_UNAVAILABLE: LegacyMigrationMessages.legacyMigrationErrorDataSourceUnavailable,
  LEGACY_MIGRATION_DATA_SOURCE_ACCESS_DENIED: LegacyMigrationMessages.legacyMigrationErrorDataSourceAccessDenied,
  LEGACY_MIGRATION_MODULE_NOT_CONTRACTED: LegacyMigrationMessages.legacyMigrationErrorModuleNotContracted,
  LEGACY_MIGRATION_INVALID_REQUEST: LegacyMigrationMessages.legacyMigrationErrorInvalidRequest,
  LEGACY_MIGRATION_FLEET_NOT_CONFIGURED: LegacyMigrationMessages.legacyMigrationErrorFleetNotConfigured,
  LEGACY_MIGRATION_FLEET_UNAVAILABLE: LegacyMigrationMessages.legacyMigrationErrorFleetUnavailable,
  LEGACY_MIGRATION_FLEET_TOKEN_REJECTED: LegacyMigrationMessages.legacyMigrationErrorFleetTokenRejected,
  LEGACY_MIGRATION_FLEET_OUTDATED: LegacyMigrationMessages.legacyMigrationErrorFleetOutdated,
  LEGACY_MIGRATION_FLEET_CORPORATE_SSO: LegacyMigrationMessages.legacyMigrationErrorFleetCorporateSso,

  // Staff-permission codes (EAnnouncementError) returned by the "manage access"
  // routes. They contain dots and spaces, so they are mapped to our own keys
  // rather than used as i18n keys.
  "0.3.2 (TCON303332)": LegacyMigrationMessages.legacyMigrationStaffErrorForbidden,
  "0.3.4 (TCON303334)": LegacyMigrationMessages.legacyMigrationStaffErrorUserNotFound,
  "0.3.5 (TCON303335)": LegacyMigrationMessages.legacyMigrationStaffErrorTargetNotAdmin,
  "0.3.7 (TCON303337)": LegacyMigrationMessages.legacyMigrationStaffErrorCannotRevokeSelf
};

/** Outcome of one user (`CREATED`, `SKIPPED`, …) in the operator's language. */
export const translateOutcome = (t: TFunction, outcome: string): string => (
  OUTCOME_MESSAGE[outcome] ? String(t(OUTCOME_MESSAGE[outcome])) : outcome
);

/** Translates `CODE` or `CODE|detail` through a map; an unknown code is shown raw rather than hidden. */
const translateCode = (
  t: TFunction,
  messages: Record<string, LegacyMigrationMessages>,
  value?: string | null
): string => {
  if (!value) return "";

  const separator = value.indexOf(DETAIL_SEPARATOR);
  const code = separator < 0 ? value : value.slice(0, separator);
  const detail = separator < 0 ? "" : value.slice(separator + 1);
  const message = messages[code];

  if (!message) return value;

  return String(t(message, { detail }));
};

/**
 * Why a user was left out, or what happened to them — `CODE` or `CODE|detail`,
 * where the detail is a value that has no translation (a list of legacy areas,
 * an e-mail, the type of an exception).
 */
export const translateReason = (t: TFunction, reason?: string | null): string => (
  translateCode(t, REASON_MESSAGE, reason)
);

/** What happened to a user's legacy password (Topcon Legado or FLEET); empty when no lock was requested. */
export const translateLockReason = (t: TFunction, reason?: string | null, source: LegacySource = "CRM"): string => (
  translateCode(t, source === "FLEET" ? FLEET_LOCK_REASON_MESSAGE : LOCK_REASON_MESSAGE, reason)
);

/**
 * Rejected request. Only the code is translated: the body's own text is the
 * backend's English, and a transport failure carries no code at all, so both
 * fall back to the generic message rather than reaching the operator in
 * another language.
 */
export const translateError = (t: TFunction, code?: string | null): string => {
  const message = code ? ERROR_MESSAGE[code] : undefined;

  return String(t(message ?? LegacyMigrationMessages.legacyMigrationErrorUnknown));
};
